import { Fragment, memo, useCallback, useEffect, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { InsightIcon, KnowledgeCenterOutlineIcon, MyProfileIcon } from 'basic-components/icons';
import { CommonService } from 'dashboard-services/services';
import { DashboardLinkFactory } from 'ng-common-features/urlBuilder';
import { globalActions } from 'primary-components';

import Element from './Element';
import Header from './Header';
import Title from './Title';

import { When } from 'react-if';

import classNames from 'classnames';
import './SideBar.scss';

const SideBar = () => {
  const dispatch = useDispatch(),
        timeout = useRef(),
        hoverTimeout = useRef(),
        navigate = useNavigate(),

        fetchingRequests = useSelector(state => state.globalState.fetchingRequests),

        [animationStarted, setAnimationStarted] = useState(false),
        [expanded, setExpanded] = useState(false),
        [action, setAction] = useState(undefined),

        setExpandedFalse = useCallback(() => {
          setExpanded(false)
          setAction('click')
        }, []),
        changeExpanded = useCallback(() => {
          if(!expanded) {
            setAnimationStarted(true)
            clearTimeout(timeout.current)
            timeout.current = setTimeout(() => setAnimationStarted(false), 201)
          }
          setExpanded(e => !e)
          setAction('click')
        }, [expanded]),
        startTimer = useCallback(() => {
          if(!expanded && !hoverTimeout.current) {
            hoverTimeout.current = setTimeout(() => {
              setExpanded(e => {
                if(!e) {
                  setAction('hover')
                  setAnimationStarted(true)
                  clearTimeout(timeout.current)
                  timeout.current = setTimeout(() => setAnimationStarted(false), 201)
                }
                return true
              })
            }, 1500)
          }
        }, [hoverTimeout, expanded]),
        clearTimer = useCallback(() => {
          clearTimeout(hoverTimeout.current)
          hoverTimeout.current = null;
          if(action === 'hover' && expanded) {
            setExpanded(false)
          }
        }, [hoverTimeout, expanded, action]),
        show = useCallback(path => {
          clearTimer()
          fetchingRequests.forEach(f => {
            f.abortController?.abort?.()
            dispatch(globalActions.handleRequest({ id: f.id, isResponse: true }));
          })
          batch(() => {
            dispatch(globalActions.clearRequests())
            navigate(path)
          })
          setExpandedFalse()
        }, [fetchingRequests, setExpandedFalse, clearTimer, dispatch, navigate]),
        learnMore = useCallback(() => CommonService.openInNewTab(DashboardLinkFactory.knowledgeCenter.tasks({ baseUrl: "" })), [])

  useEffect(() => () => {
    clearTimeout(hoverTimeout.current)
    clearTimeout(timeout.current)
  }, [])

  useEffect(() => {
    setExpandedFalse()
  }, [setExpandedFalse])

  return (
    <Fragment>
      <div 
          className={classNames("sidebar", { "sidebar--is-expanded": expanded }, { "sidebar--discovery-only": true })}
          onMouseEnter={startTimer}
          onMouseLeave={clearTimer}
      >
        <div className="sidebar__top">
          <Title
              animationStarted={animationStarted}
              expanded={expanded}
              setExpandedFalse={setExpandedFalse}
              switchExpanded={changeExpanded}
          />
        </div>
        <div className={classNames("sidebar__bottom", { "sidebar__bottom--discovery-only": true })}>
          <div className="sidebar__group">
            <Header
                animationStarted={animationStarted}
                expanded={expanded}
                onDotClick={changeExpanded}
                text="DISCOVER"
            />
            <Element
                animationStarted={animationStarted}
                expanded={expanded}
                icon={<KnowledgeCenterOutlineIcon/>}
                onClick={learnMore}
                selected={false}
                text="KNOWLEDGE"
            />
            <Element
                animationStarted={animationStarted}
                expanded={expanded}
                icon={<InsightIcon/>}
                onClick={show}
                path={window.location.pathname}
                selected
                text="INSIGHT"
            />
          </div>
          <div className="sidebar__group">
            <Header
                animationStarted={animationStarted}
                expanded={expanded}
                onDotClick={changeExpanded}
                text="USER"
            />
            <Element
                animationStarted={animationStarted}
                expanded={expanded}
                icon={<MyProfileIcon/>}
                onClick={show}
                path={DashboardLinkFactory.insight.list({ baseUrl: "" })}
                selected={false}
                text="LOG IN"
            />
          </div>
        </div>
      </div>
      <When condition={expanded && !animationStarted}>
        {() => (
          <div
              className="sidebar-background"
              onClick={setExpandedFalse}
          />
        )}
      </When>
    </Fragment>
  )
}

export default memo(SideBar)
